import { useCallback, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useEditMode,
  useSlotGroup,
} from '@vibesites/runtime/react';
import {
  getMediaUrl,
  parseStorageKey,
  videoThumbStorageKey,
  VibesitesImage,
} from '@vibesites/ui-primitives';

/** Convert an Instagram reel/post permalink into an embed iframe src. */
function toInstagramEmbedSrc(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    if (!url.hostname.replace(/^www\./, '').endsWith('instagram.com')) return null;
    const match = url.pathname.match(/\/(reel|reels|p|tv)\/([A-Za-z0-9_-]+)/i);
    if (!match) return null;
    const kind = match[1].toLowerCase() === 'reels' ? 'reel' : match[1].toLowerCase();
    return `https://www.instagram.com/${kind}/${match[2]}/embed/`;
  } catch {
    return null;
  }
}

function isPlayableNativeVideo(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const host = new URL(url).hostname;
    // Instagram/Facebook CDN mp4s block third-party playback
    if (host.includes('cdninstagram.com') || host.includes('fbcdn.net')) return false;
    return true;
  } catch {
    return !/cdninstagram\.com|fbcdn\.net/i.test(url);
  }
}

const reel1Def: SlotGroupDefinition = {
  label: 'Resident Video 1',
  description:
    'Paste an Instagram reel permalink, or pick a Multihub-hosted video. Instagram CDN .mp4 links will not play.',
  fields: {
    permalink: {
      label: 'Instagram Reel URL',
      type: 'url',
      defaultValue: 'https://www.instagram.com/p/DcPGBP9Fa6-/',
      suggestedFields: ['property_instagram_url'],
    },
    video: {
      label: 'Multihub Video (optional)',
      type: 'video',
      defaultValue: '',
      suggestedFields: ['property_featured_video'],
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
    poster: {
      label: 'Poster Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
    },
  },
};

const reel2Def: SlotGroupDefinition = {
  label: 'Resident Video 2',
  description:
    'Paste an Instagram reel permalink, or pick a Multihub-hosted video. Instagram CDN .mp4 links will not play.',
  fields: {
    permalink: {
      label: 'Instagram Reel URL',
      type: 'url',
      defaultValue: 'https://www.instagram.com/p/Db9iYCoMHOY/',
      suggestedFields: ['property_instagram_url'],
    },
    video: {
      label: 'Multihub Video (optional)',
      type: 'video',
      defaultValue: '',
      suggestedFields: ['property_featured_video'],
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
    poster: {
      label: 'Poster Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
    },
  },
};

const socialProfileDef: SlotGroupDefinition = {
  label: 'Social Profile',
  description: 'Shown above Multihub-hosted videos (Instagram embeds include their own header).',
  fields: {
    avatar: {
      label: 'Avatar',
      type: 'image',
      defaultField: 'property_primary_logo',
    },
    username: {
      label: 'Username',
      type: 'text',
      defaultValue: '1221broadway',
    },
    caption: {
      label: 'Caption',
      type: 'text',
      defaultValue: 'Original audio',
    },
    url: {
      label: 'Profile URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      defaultValue: 'https://www.instagram.com/1221broadway/',
    },
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'View profile',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

function hasText(val: unknown): boolean {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined';
}

function resolvePoster(videoUrl: string | null | undefined, posterUrl: string | null | undefined) {
  const thumbKey = videoThumbStorageKey(parseStorageKey(videoUrl || null));
  return (thumbKey ? getMediaUrl(thumbKey, { width: 720 }) : null) || posterUrl || '';
}

function SocialHeader({
  profile,
}: {
  profile: {
    avatar?: string | null;
    username?: string | null;
    caption?: string | null;
    url?: string | null;
    label?: string | null;
    _target?: string | null;
  };
}) {
  const href = profile.url || 'https://www.instagram.com/1221broadway/';
  const target = profile._target || '_blank';
  const username = profile.username || '1221broadway';
  const caption = profile.caption || 'Original audio';
  const buttonLabel = profile.label || 'View profile';

  return (
    <div className="lifestyle-social-header">
      <a
        href={href}
        target={target}
        rel="noopener noreferrer"
        className="lifestyle-social-identity"
        aria-label={`${username} on Instagram`}
      >
        <span className="lifestyle-social-avatar" aria-hidden={!profile.avatar}>
          {profile.avatar ? (
            <VibesitesImage
              url={profile.avatar}
              alt=""
              width={32}
              height={32}
              sizes="32px"
              className="lifestyle-social-avatar-img"
            />
          ) : null}
        </span>
        <span className="lifestyle-social-text">
          <span className="lifestyle-social-username">{username}</span>
          <span className="lifestyle-social-caption">{caption}</span>
        </span>
      </a>
      <a href={href} target={target} rel="noopener noreferrer" className="lifestyle-social-cta">
        {buttonLabel}
      </a>
    </div>
  );
}

function NativeReelVideo({
  src,
  poster,
  title,
}: {
  src: string;
  poster?: string;
  title: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  const togglePlay = useCallback(async () => {
    const el = videoRef.current;
    if (!el || failed) return;
    try {
      if (el.paused) {
        await el.play();
        setPlaying(true);
      } else {
        el.pause();
        setPlaying(false);
      }
    } catch {
      setFailed(true);
    }
    if (el.error) setFailed(true);
  }, [failed]);

  return (
    <div className="lifestyle-reel-frame">
      <video
        ref={videoRef}
        className="lifestyle-reel-video"
        src={src}
        poster={poster || undefined}
        controls={playing}
        playsInline
        preload="metadata"
        title={title}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onClick={(e) => {
          if (playing) return;
          e.preventDefault();
          void togglePlay();
        }}
      />
      {!playing && !failed && (
        <button
          type="button"
          className="lifestyle-reel-play"
          aria-label={`Play ${title}`}
          onClick={() => void togglePlay()}
        >
          <span className="lifestyle-reel-play-icon" aria-hidden />
        </button>
      )}
      {failed && (
        <div className="lifestyle-reel-error">
          <p>This video couldn&apos;t be played.</p>
          <p className="lifestyle-reel-error-hint">
            Pick a Multihub-hosted file, or paste an Instagram reel permalink.
          </p>
        </div>
      )}
    </div>
  );
}

function resolveReelMode(reel: {
  video?: string | null;
  permalink?: string | null;
  poster?: string | null;
}) {
  const rawVideo = hasText(reel.video) ? String(reel.video) : null;
  const nativeVideo = rawVideo && isPlayableNativeVideo(rawVideo) ? rawVideo : null;
  const embedSrc = toInstagramEmbedSrc(reel.permalink);

  // Multihub-hosted native video wins when available; otherwise Instagram embed
  if (nativeVideo) {
    return {
      playableVideo: nativeVideo,
      embedSrc: null as string | null,
      poster: resolvePoster(nativeVideo, reel.poster),
      showNative: true,
      showEmbed: false,
    };
  }

  return {
    playableVideo: null as string | null,
    embedSrc,
    poster: resolvePoster(null, reel.poster),
    showNative: false,
    showEmbed: !!embedSrc,
  };
}

export function LifestyleTestimonials() {
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const reel1 = useSlotGroup('lifestyletestimonials.reel1', reel1Def);
  const reel2 = useSlotGroup('lifestyletestimonials.reel2', reel2Def);
  const profile = useSlotGroup('lifestyletestimonials.social_profile', socialProfileDef);

  const mode1 = resolveReelMode(reel1);
  const mode2 = resolveReelMode(reel2);

  return (
    <section className="lifestyle-testimonials bg-background text-foreground py-16 md:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <RichTextContent
            path="lifestyletestimonials.title"
            label="Title"
            defaultValue="<p>Life at 1221 Broadway</p>"
            as="h2"
            className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-wider leading-tight"
          />
          <RichTextContent
            path="lifestyletestimonials.subtitle"
            label="Subtitle"
            defaultValue="<p>See what our residents are saying</p>"
            as="div"
            className="font-body text-base md:text-lg text-muted-foreground mt-4"
          />
        </div>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 justify-items-center">
          {(isEditMode || mode1.showNative || mode1.showEmbed) && (
            <div className="lifestyle-reel">
              {mode1.showNative && (
                <EditableSlotGroup
                  groupPath="lifestyletestimonials.social_profile"
                  definition={socialProfileDef}
                >
                  <SocialHeader profile={profile} />
                </EditableSlotGroup>
              )}
              <EditableSlotGroup groupPath="lifestyletestimonials.reel1" definition={reel1Def}>
                {mode1.showNative ? (
                  <NativeReelVideo
                    src={mode1.playableVideo!}
                    poster={mode1.poster || undefined}
                    title="Resident video 1"
                  />
                ) : mode1.showEmbed ? (
                  <div className="lifestyle-reel-embed-wrap">
                    <iframe
                      src={mode1.embedSrc!}
                      title="Resident video 1"
                      className="lifestyle-reel-embed"
                      loading="lazy"
                      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="lifestyle-reel-placeholder">
                    <p className="font-body text-sm text-muted-foreground">
                      Paste an Instagram reel URL (instagram.com/reel/…), or select a Multihub
                      video.
                    </p>
                  </div>
                )}
              </EditableSlotGroup>
            </div>
          )}

          {(isEditMode || mode2.showNative || mode2.showEmbed) && (
            <div className="lifestyle-reel">
              {mode2.showNative && (
                <EditableSlotGroup
                  groupPath="lifestyletestimonials.social_profile"
                  definition={socialProfileDef}
                >
                  <SocialHeader profile={profile} />
                </EditableSlotGroup>
              )}
              <EditableSlotGroup groupPath="lifestyletestimonials.reel2" definition={reel2Def}>
                {mode2.showNative ? (
                  <NativeReelVideo
                    src={mode2.playableVideo!}
                    poster={mode2.poster || undefined}
                    title="Resident video 2"
                  />
                ) : mode2.showEmbed ? (
                  <div className="lifestyle-reel-embed-wrap">
                    <iframe
                      src={mode2.embedSrc!}
                      title="Resident video 2"
                      className="lifestyle-reel-embed"
                      loading="lazy"
                      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="lifestyle-reel-placeholder">
                    <p className="font-body text-sm text-muted-foreground">
                      Paste an Instagram reel URL (instagram.com/reel/…), or select a Multihub
                      video.
                    </p>
                  </div>
                )}
              </EditableSlotGroup>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .lifestyle-reel {
          width: 100%;
          max-width: 340px;
        }
        .lifestyle-social-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          padding: 0.75rem 0.85rem;
          margin-bottom: 0;
          background: #fff;
          border: 1px solid #dbdbdb;
          border-bottom: none;
          border-radius: 4px 4px 0 0;
        }
        .lifestyle-social-identity {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          min-width: 0;
          text-decoration: none;
          color: inherit;
        }
        .lifestyle-social-avatar {
          flex-shrink: 0;
          width: 32px;
          height: 32px;
          border-radius: 9999px;
          overflow: hidden;
          background: #f0f0f0;
          border: 1px solid #e0e0e0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .lifestyle-social-avatar-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .lifestyle-social-text {
          display: flex;
          flex-direction: column;
          min-width: 0;
          line-height: 1.2;
        }
        .lifestyle-social-username {
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 0.875rem;
          font-weight: 600;
          color: #262626;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .lifestyle-social-caption {
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 0.75rem;
          color: #8e8e8e;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .lifestyle-social-cta {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.35rem 0.75rem;
          border-radius: 8px;
          background: #0095f6;
          color: #fff;
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 0.8125rem;
          font-weight: 600;
          line-height: 1.2;
          text-decoration: none;
          white-space: nowrap;
        }
        .lifestyle-social-cta:hover {
          background: #1877f2;
        }
        .lifestyle-reel-frame {
          position: relative;
          width: 100%;
          aspect-ratio: 9 / 16;
          border-radius: 0 0 4px 4px;
          overflow: hidden;
          background: #000;
          border: 1px solid #dbdbdb;
          border-top: none;
        }
        .lifestyle-reel-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .lifestyle-reel-play {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: 0;
          cursor: pointer;
          z-index: 2;
        }
        .lifestyle-reel-play-icon {
          width: 64px;
          height: 64px;
          border-radius: 9999px;
          background: rgba(0, 0, 0, 0.45);
          border: 2px solid rgba(255, 255, 255, 0.95);
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
          position: relative;
        }
        .lifestyle-reel-play-icon::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 54%;
          transform: translate(-50%, -50%);
          border-style: solid;
          border-width: 10px 0 10px 16px;
          border-color: transparent transparent transparent #fff;
        }
        .lifestyle-reel-error {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 1.5rem;
          text-align: center;
          background: rgba(0, 0, 0, 0.72);
          color: #fff;
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 0.875rem;
          z-index: 3;
        }
        .lifestyle-reel-error-hint {
          font-size: 0.75rem;
          opacity: 0.8;
        }
        .lifestyle-reel-embed-wrap {
          width: 100%;
          border: 1px solid #dbdbdb;
          border-radius: 4px;
          overflow: hidden;
          background: #fff;
          min-height: 620px;
        }
        .lifestyle-reel-embed {
          width: 100%;
          min-height: 620px;
          height: 620px;
          border: 0;
          display: block;
          background: #fff;
        }
        .lifestyle-reel-placeholder {
          aspect-ratio: 9 / 16;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          text-align: center;
          background: var(--vibe-color-surface);
          border: 1px dashed var(--vibe-color-border);
          border-radius: 4px;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'LifestyleTestimonials',
  displayName: 'Lifestyle Testimonials',
  tokens: {
    'lifestyletestimonials.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Life at 1221 Broadway</p>',
    },
    'lifestyletestimonials.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>See what our residents are saying</p>',
    },
  },
  slotGroups: {
    'lifestyletestimonials.reel1': reel1Def,
    'lifestyletestimonials.reel2': reel2Def,
    'lifestyletestimonials.social_profile': socialProfileDef,
  },
};

(LifestyleTestimonials as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default LifestyleTestimonials;
